import classNames from 'classnames';
import React from 'react';
import Slider from 'react-slick';
import { useParams } from 'react-router';

import { Product } from '../../../../types/working-model';
import { Button } from '../../../general-ui/button/button';
import { PrevArrow, NextArrow } from '../../../general-ui/arrows/arrows';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { stylingOverrideReturns } from '../../utils';
import { ParamsProps } from '../../live-event';
import { settings } from '../../../../utils/slider-utils';
import { useTranslate } from '../../../../i18n/useTranslationModules';
import useTranslationRoute from '../../hooks/use-translation-route';
import { ProductWrapper } from './product-wrapper';
import { shouldShowBuyNow } from '../../../../utils/products';
import { OptionalComponent } from '../../../../utils/optional-component';


interface ProductItemProps {
	module: Product;
	stylingOverrides: stylingOverrideReturns;
	template: string;
	onClick: (product: Product) => void;
	isLandingPage?: boolean;
}

const ProductItem: React.FC<ProductItemProps> = ({ module, stylingOverrides, template, onClick }) => {
	const params: ParamsProps = useParams();
	const language = params.language || 'en';
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate([translationRoute, "session"]);
	const {
		price,
		sale_price,
		product_title,
		description,
		images,
		button_text
	} = module;

	const {
		moduleTitleOverrides,
		moduleCustomHeadingOverrides,
		moduleDescriptionOverrides,
		primaryButtonOverrides
	} = stylingOverrides;

	const id = module.module_id;
	const transKey = `${translationRoute}:product.${id}`;

	const defaultButtonText = t('session:Buy Now'); //TODO: this should probably change but we need to sort out translation for product cards
	const shouldDisplayBuyNow = shouldShowBuyNow(module.buy_now_link, module.settings?.bypass_modal);

	// In the case with an image carousel, you dont want the arrows on the carousel to open the modal
	const multiImageAndModalOpenerFunction = !module.settings?.bypass_modal && module.images.length > 1
		? () => onClick(module)
		: () => null;

	return (
		<ProductWrapper item={module} onClick={onClick}>
			<div className={classNames('product-item-image-container')}>
				<Slider
					prevArrow={<PrevArrow />}
					nextArrow={<NextArrow />}
					{...settings}
				>
					{images.map((image: string, index: number) => (
						<React.Fragment key={index}>
							<img src={image} alt={image} aria-hidden="true" onClick={multiImageAndModalOpenerFunction} />
						</React.Fragment>
					))}
				</Slider>
				{
					sale_price?.[language] && (
						<span className="product-item-label">{t('session:Sale')}</span>
					)
				}
			</div>
			<div className="product-item-content" onClick={multiImageAndModalOpenerFunction}>
				<TypographyItem
					className={classNames('product-item-title stable', moduleTitleOverrides)}
					tagName="h3"
				>
					{t(`${transKey}.title`, product_title[language] as string ?? '')}
				</TypographyItem>
				{/* TODO: implement the currency */}
				<div className="price-container">
					<TypographyItem
						className={classNames('product-item-price stable', moduleCustomHeadingOverrides)}
					>
						{price?.base ? (
							`$ ${t(`${transKey}.sale_price`, (sale_price?.[language] || price?.[language]) ?? '')}`
						) : null}
					</TypographyItem>
					{(sale_price?.base && price?.base) ? (
						<TypographyItem className={classNames('product-item-old-price evt-body-text-2 stable', moduleDescriptionOverrides)}>
							$ {t(`${transKey}.price`, price?.[language] ?? '')}
						</TypographyItem>
					) : null
					}
				</div>
				<TypographyItem className={classNames(
					'evt-body-text-2 product-item-description',
					moduleDescriptionOverrides,
					{ 'increase-description': !price?.base }
				)} tagName="p">
					{t(`${transKey}.description`, description?.[language] as string)}
				</TypographyItem>
				<OptionalComponent display={shouldDisplayBuyNow}>
					<Button
						classButton={classNames(
							'primary',
							'buy-now-btn',
							primaryButtonOverrides
						)}
						template={template}
					>
						{t(`${transKey}.button_text`, button_text?.[language] as string || defaultButtonText)}
					</Button>
				</OptionalComponent>
			</div>
		</ProductWrapper>
	);
};

export default ProductItem;
